// ============================================================
// ProjectsList.jsx — saved video projects (the editor's home view)
// ------------------------------------------------------------
// Lists the client's video projects via /api/video/projects, newest first.
// "New Project" hands control back to the clip browser; clicking a card reopens
// that project (the panel restores full state via useVideoEditor.loadProject).
// Each card shows title, render status, and a short summary derived from
// edit_state.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../../lib/authedFetch';
import { useViewingAs } from '../../hooks/useViewingAs';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
};

const summarize = (row) => {
  const es = row.edit_state || {};
  const overlays = Array.isArray(es.textOverlays) ? es.textOverlays.length : 0;
  const bits = [];
  bits.push(overlays === 1 ? '1 text overlay' : `${overlays} text overlays`);
  if (es.music && es.music.title) bits.push(`♪ ${es.music.title}`);
  if (es.filterPreset && es.filterPreset !== 'Normal') bits.push(es.filterPreset);
  return bits.join(' · ');
};

export default function ProjectsList({ clientId, onOpen, onNew, onUseInPost }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [renamingId, setRenamingId]   = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const { viewingAsClientId } = useViewingAs();

  // Phase 7. `clientId` is the SIGNED-IN user (useClientId, via
  // VideoEditorPanel), so this listed the ADMIN's projects while an admin
  // browsed a client's editor. Scope comes from the route now; the prop stays
  // as the "workspace loaded yet" guard it already was.
  //
  // The effect below depends on [load], so adding viewingAsClientId to THIS
  // array is the only change needed to make the list refetch when an admin
  // switches client — the effect follows for free. That is what made this the
  // cleanest of the four files in this phase.
  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // No ?status= — this list wants every project, not just completed
      // renders. The Post Generator's picker passes the filter.
      const res = await clientScopedFetch(`${BACKEND}/api/video/projects`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setProjects(body.projects || []);
    } catch (err) {
      setError(err?.message || 'Could not load your projects');
    }
    setLoading(false);
  }, [clientId, viewingAsClientId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = useCallback(async (row, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${row.title}"? This can't be undone.`)) return;
    setProjects((prev) => prev.filter((p) => p.id !== row.id));
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/projects/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      setError(`Could not delete project: ${err?.message || err}`);
      load();
    }
  }, [load]);

  const startRename = useCallback((row, e) => {
    e.stopPropagation();
    setRenamingId(row.id);
    setRenameValue(row.title || '');
  }, []);

  const cancelRename = useCallback((e) => {
    e?.stopPropagation();
    setRenamingId(null);
  }, []);

  const commitRename = useCallback(async (row) => {
    const next = renameValue.trim();
    setRenamingId(null);
    if (!next || next === row.title) return;
    setProjects((prev) => prev.map((p) => (p.id === row.id ? { ...p, title: next } : p)));
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/projects/${encodeURIComponent(row.id)}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ patch: { title: next } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      setError(`Could not rename project: ${err?.message || err}`);
      load();
    }
  }, [renameValue, load]);

  return (
    <div className="ve-browser">
      <div className="ve-browser-toolbar">
        <div className="ve-browser-title">Your video projects</div>
        <div className="ve-browser-actions">
          <button className="ve-btn ve-btn-primary ve-btn-sm" onClick={onNew} disabled={!clientId}>
            ＋ New Project
          </button>
        </div>
      </div>

      {error && <div className="ve-browser-error">{error}</div>}

      {loading ? (
        <div className="ve-browser-empty">
          <div className="ve-empty-sub">Loading your projects…</div>
        </div>
      ) : projects.length === 0 ? (
        <div className="ve-browser-empty">
          <div className="ve-empty-icon" aria-hidden="true">🎬</div>
          <div className="ve-empty-title">No projects yet</div>
          <div className="ve-empty-sub">Start a new project to edit a clip, add text, music, and render.</div>
          <button
            className="ve-btn ve-btn-primary ve-btn-sm"
            onClick={onNew}
            disabled={!clientId}
            style={{ marginTop: 14 }}
          >
            ＋ New Project
          </button>
        </div>
      ) : (
        <div className="ve-proj-grid">
          {projects.map((row) => {
            const isCompleted = row.render_status === 'completed';
            return (
            <div key={row.id} className="ve-proj-card" onClick={() => onOpen?.(row)}>
              {isCompleted && (
                <div className="ve-proj-thumb">
                  {row.thumbnail_url ? (
                    <img src={row.thumbnail_url} alt={row.title || 'Video'} />
                  ) : (
                    <div className="ve-proj-thumb-placeholder" aria-hidden="true">
                      <span className="ve-proj-play">▶</span>
                    </div>
                  )}
                </div>
              )}
              <div className="ve-proj-head">
                {renamingId === row.id ? (
                  <input
                    className="ve-rename-input"
                    value={renameValue}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(row); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelRename(e); }
                    }}
                  />
                ) : (
                  <span className="ve-proj-title">{row.title || 'Untitled Video'}</span>
                )}
                {row.render_status && row.render_status !== 'draft' && (
                  <span className={`ve-status ve-status-${row.render_status}`}>{row.render_status}</span>
                )}
              </div>
              <div className="ve-proj-sub">{summarize(row)}</div>
              <div className="ve-proj-foot">
                <span className="ve-proj-date">Updated {fmtDate(row.updated_at)}</span>
                <span className="ve-proj-foot-actions">
                  <button
                    className="ve-icon-btn"
                    onClick={(e) => startRename(row, e)}
                    title="Rename project"
                  >✎</button>
                  <button
                    className="ve-proj-del"
                    onClick={(e) => handleDelete(row, e)}
                    title="Delete project"
                  >✕</button>
                </span>
              </div>
              {isCompleted && (
                <div className="ve-proj-actions" onClick={(e) => e.stopPropagation()}>
                  {row.output_url && (
                    <a
                      className="ve-btn ve-btn-ghost ve-btn-sm"
                      href={row.output_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >↓ Download</a>
                  )}
                  <button
                    className="ve-btn ve-btn-primary ve-btn-sm"
                    onClick={() => onUseInPost?.(row)}
                    title="Use this video in a new post"
                  >→ Use in Post</button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
